import { useMemo } from "react";
import { useEntryFiltersCache, useMattersCache } from "@/shared/cache";
import { useApplicationsCache } from "@/shared/cache/slices/applications";
import { matterNumber } from "@/shared/utils/matterLabels";
import type { Entry } from "../normalize";
import { formatTimekeeperFullName, type Timekeeper } from "../timekeepers";
import type { FieldOptions } from "./FilterMenu";
import { FIELD_DEFS, type OptionItem } from "./fields";
import type { EnumFilterField } from "./types";

// "Photos" is no longer tracked, and the synthetic stacking apps (Group,
// Merge, Collapse) are actions rather than tracked applications; hide them
// all from the app filter.
const HIDDEN_APPS = new Set([
	"",
	"Phone",
	"Hourglass",
	"Photos",
	"Group",
	"Merge",
	"Collapse",
]);
const BOTTOM_APPS = ["User Input", "Duplicate", "Merge", "Collapse", "Group"];
const BOTTOM_APP_ORDER = new Map(BOTTOM_APPS.map((name, i) => [name, i]));

// Shared collator. Inline `localeCompare(undefined, { sensitivity: "base" })`
// builds a fresh collator per call; on a firm-sized matter list (5k+) that
// adds ~150ms per sort. Reusing one instance drops it to a few ms.
const COLLATOR = new Intl.Collator(undefined, { sensitivity: "base" });

function sortAppNames(names: Iterable<string>): string[] {
	return Array.from(names).sort((a, b) => {
		const ai = BOTTOM_APP_ORDER.get(a);
		const bi = BOTTOM_APP_ORDER.get(b);
		if (ai !== undefined && bi !== undefined) return ai - bi;
		if (ai !== undefined) return 1;
		if (bi !== undefined) return -1;
		return COLLATOR.compare(a, b);
	});
}

/**
 * Build the value option list per enum filter field. Static fields use the
 * registry (FIELD_DEFS); data-driven fields are deduped + sorted from loaded
 * entries. Applications come from the canonical /applications cache. Matters
 * and clients come from the firm matters cache and are unioned with
 * entry-derived values so that orphan codes (matters referenced by an entry
 * but no longer in the cache) still appear.
 */
export function useFilterOptions(
	entries: ReadonlyArray<Entry>,
	timekeepers?: readonly Timekeeper[] | null,
): FieldOptions {
	const { applications: knownApplications } = useApplicationsCache();
	const { matters: cachedMatters } = useMattersCache();
	const { entryFilters } = useEntryFiltersCache();

	return useMemo(() => {
		const staticOptions: Partial<
			Record<EnumFilterField, ReadonlyArray<string>>
		> = {};
		for (const def of FIELD_DEFS) {
			if (def.kind !== "enum") continue;
			if (def.source === "static" && def.staticValues) {
				staticOptions[def.key] = def.staticValues;
			}
		}

		const appNames = new Set<string>();
		for (const a of knownApplications) {
			if (a.application && !HIDDEN_APPS.has(a.application)) {
				appNames.add(a.application);
			}
		}
		for (const e of entries) {
			if (e.application && !HIDDEN_APPS.has(e.application)) {
				appNames.add(e.application);
			}
		}

		// Filter value is the matter code, matching the matter column's
		// accessor (`e.matter?.code`). Cache rows ship a sublabel with the
		// matter description + client name.
		const matterByCode = new Map<string, OptionItem>();
		for (const m of cachedMatters) {
			const code = matterNumber(m);
			if (!code) continue;
			if (!matterByCode.has(code)) {
				const sub = [m.description?.trim(), m.client?.trim()]
					.filter(Boolean)
					.join(" · ");
				matterByCode.set(code, {
					value: code,
					label: code,
					sublabel: sub || undefined,
				});
			}
		}
		for (const e of entries) {
			const code = e.matter?.code ?? null;
			if (!code) continue;
			if (matterByCode.has(code)) continue;
			matterByCode.set(code, {
				value: code,
				label: code,
				sublabel: e.matter?.client ?? undefined,
			});
		}
		const matterOptions = Array.from(matterByCode.values()).sort((a, b) =>
			COLLATOR.compare(a.label, b.label),
		);

		const clientSet = new Set<string>();
		for (const m of cachedMatters) if (m.client) clientSet.add(m.client);
		for (const e of entries)
			if (e.matter?.client) clientSet.add(e.matter.client);
		const clientOptions = Array.from(clientSet).sort(COLLATOR.compare);

		const timekeeperOptions = timekeepers?.map((timekeeper) => ({
			value: timekeeper.id,
			label: formatTimekeeperFullName(timekeeper),
			sublabel: timekeeper.email,
		}));

		const applicationOptions =
			entryFilters.application.length > 0
				? sortAppNames(
						entryFilters.application.filter((name) => !HIDDEN_APPS.has(name)),
					)
				: sortAppNames(appNames);
		const serverMatterOptions =
			entryFilters.matter as ReadonlyArray<OptionItem>;
		const serverTimekeeperOptions =
			entryFilters.timekeeper.length > 1
				? (entryFilters.timekeeper as ReadonlyArray<OptionItem>)
				: undefined;

		return {
			...staticOptions,
			...(entryFilters.status.length > 0
				? { status: entryFilters.status }
				: {}),
			// "Credit" is retired in the UI; the server list still advertises
			// billable_credit, so drop it before it reaches the menu.
			...(entryFilters.billableCategory.length > 0
				? {
						billableCategory: entryFilters.billableCategory.filter(
							(value) => value !== "billable_credit",
						),
					}
				: {}),
			...(timekeeperOptions && timekeeperOptions.length > 0
				? { timekeeper: timekeeperOptions }
				: serverTimekeeperOptions
					? { timekeeper: serverTimekeeperOptions }
					: {}),
			application: applicationOptions,
			matter:
				serverMatterOptions.length > 0 ? serverMatterOptions : matterOptions,
			client:
				entryFilters.client.length > 0 ? entryFilters.client : clientOptions,
		};
	}, [entries, knownApplications, cachedMatters, timekeepers, entryFilters]);
}
